import {
  CustomerContractInventoryGridItem,
  CustomerContractSummaryGrossNetGridItem,
  ContractForm,
  ContractDocument,
  TERM_BUCKET,
  TERM_TYPE,
} from 'shared/types';

export function mapContractsToContractInventoryGridItems(
  contracts: ContractDocument[] | undefined
): CustomerContractInventoryGridItem[] {
  return (
    contracts?.map(contract => {
      return {
        id: contract.id,
        brand: contract.brand,
        status: contract.status,
        approvalStatus: contract.approvalStatus,
        billToCode: contract.billToCode,
        startDate: contract.startDate,
        endDate: contract.endDate,
        customerName: contract.customerName,
        originalDocumentName: contract.originalDocumentName,
        deletedAt: contract.deletedAt,
        deletedBy: contract.deletedBy,
        approvedDate: contract.approvedDate,
      };
    }) ?? []
  );
}

export function calculateTermDollarImpact(termType: TERM_TYPE, value: number, ltmRev: number): number {
  if (!ltmRev || !value) return 0;

  switch (termType) {
    case 'dollar':
      return value;
    case 'percent':
      return ltmRev * value;
    default:
      return 0;
  }
}

export function calculateTermPercentImpact(termType: TERM_TYPE, value: number, ltmRev: number): number {
  if (!ltmRev || !value) return 0;

  switch (termType) {
    case 'dollar':
      return value / ltmRev;
    case 'percent':
      return value;
    default:
      return 0;
  }
}

export function mapContractTermsToContractGrossNetGridItems(
  isPercentCalculation: boolean,
  termCalculationFunction: (termType: TERM_TYPE, value: number, ltmRev: number) => number,
  contract: ContractDocument
): CustomerContractSummaryGrossNetGridItem {
  const finalSeries: Record<Exclude<TERM_BUCKET, 'net payment days' | 'payment terms discount days'>, number> = {
    allowances: 0,
    'co-op': 0,
    discounts: 0,
    other: 0,
    'payment terms discount': 0,
    rebates: 0,
  };
  const result = contract.terms.reduce((finalSeries, record) => {
    switch (record.bucket) {
      case 'allowances':
        finalSeries.allowances += termCalculationFunction(record.type, record.amount, contract.l12MProductGrossRevenue);
        break;
      case 'co-op':
        finalSeries['co-op'] += termCalculationFunction(record.type, record.amount, contract.l12MProductGrossRevenue);
        break;
      case 'discounts':
        finalSeries.discounts += termCalculationFunction(record.type, record.amount, contract.l12MProductGrossRevenue);
        break;
      case 'other':
        finalSeries.other += termCalculationFunction(record.type, record.amount, contract.l12MProductGrossRevenue);
        break;
      case 'payment terms discount':
        finalSeries['payment terms discount'] += termCalculationFunction(
          record.type,
          record.amount,
          contract.l12MProductGrossRevenue
        );
        break;
      case 'rebates':
        finalSeries.rebates += termCalculationFunction(record.type, record.amount, contract.l12MProductGrossRevenue);
        break;
    }
    return finalSeries;
  }, finalSeries);

  return {
    id: contract.id,
    brand: contract.brand,
    ltmRevenue: isPercentCalculation ? 1 : contract.l12MProductGrossRevenue,
    allowances: result.allowances,
    coop: result['co-op'],
    discounts: result.discounts,
    other: result.other,
    paymentTerms: result['payment terms discount'],
    rebates: result.rebates,
    originalDocumentName: contract.originalDocumentName,
  };
}

export function contractDocumentToContractForm(contract: ContractDocument): ContractForm {
  return {
    ...contract,
    startDate: contract.startDate || null,
    endDate: contract.endDate || null,
    terms: contract.terms.map(term => ({ ...term, isNew: false, amount: String(term.amount) })),
    volumeTiers: [...contract.volumeTiers],
    growthTiers: [...contract.growthTiers],
  };
}

export function contractFormToContractDocument(form: ContractForm): ContractDocument {
  return {
    ...form,
    startDate: form.startDate || '',
    endDate: form.endDate || '',
    terms: (form.terms || []).map(({ name, display, bucket, amount, type }) => ({
      name,
      display,
      bucket,
      amount: Number(amount),
      type,
    })),
    volumeTiers: [...(form.volumeTiers || [])],
    growthTiers: [...(form.growthTiers || [])],
  };
}
